// The Google auth family's 9 connectors — v3 `utils/integrations.js` entries carrying
// `auth: 'google_email'`. BigQuery also authenticates with `google_email` but lives in
// `database.ts`, next to its three channel variants.

import type { IntegrationProvider } from '../providers.types'

export const GOOGLE_PROVIDERS: readonly IntegrationProvider[] = [
  {
    key: 'ga4_analytics_property',
    id: 'ga4_analytics_property',
    nameKey: 'integrations.providers.googleAnalytics',
    category: 'analytics_and_tracking',
    authFamily: 'google_email',
    connect: 'oauth',
    labelFields: ['label'],
    allowsMultiple: true,
    logo: 'ga4',
    configComponent: () => import('../../components/connectors/GoogleAccountConnector.vue'),
  },
  {
    key: 'google_ads_account',
    id: 'google_ads_account',
    nameKey: 'integrations.providers.googleAds',
    category: 'paid_advertising',
    authFamily: 'google_email',
    connect: 'oauth',
    labelFields: ['label', 'customer_id'],
    allowsMultiple: true,
    logo: 'google_ads',
    configComponent: () => import('../../components/connectors/GoogleAccountConnector.vue'),
  },
  {
    key: 'search_console',
    id: 'search_console',
    nameKey: 'integrations.providers.searchConsole',
    category: 'analytics_and_tracking',
    authFamily: 'google_email',
    connect: 'oauth',
    labelFields: ['url', 'site_url', 'country'],
    logo: 'google_search_console',
    configComponent: () => import('../../components/connectors/GoogleAccountConnector.vue'),
  },
  // Shaped like GA4: a listing call, then a picker. The Tag Manager scopes are OPTIONAL on the Google
  // grant, so the listing's scope refusal names `scope_context: 'tag_manager'` and the connector
  // sends the consent with it rather than a plain reauthenticate (v3 `TagManagerIntegration.vue`).
  {
    key: 'gtm_container',
    id: 'gtm_container',
    nameKey: 'integrations.providers.googleTagManager',
    category: 'analytics_and_tracking',
    authFamily: 'google_email',
    connect: 'oauth',
    labelFields: ['display_name', 'public_id'],
    allowsMultiple: true,
    logo: 'tag_manager',
    configComponent: () => import('../../components/connectors/GoogleAccountConnector.vue'),
  },
  {
    key: 'merchant_center_account',
    id: 'merchant_center_account',
    nameKey: 'integrations.providers.merchantCenter',
    category: 'meta_data',
    authFamily: 'google_email',
    connect: 'oauth',
    labelFields: ['label', 'merchant_id'],
    logo: 'merchant_center',
    configComponent: () => import('../../components/connectors/GoogleAccountConnector.vue'),
  },
  {
    key: 'youtube_channel',
    id: 'youtube_channel',
    nameKey: 'integrations.providers.youtube',
    category: 'organic_social',
    authFamily: 'google_email',
    connect: 'oauth',
    labelFields: ['title', 'channel_id'],
    logo: 'youtube',
    configComponent: () => import('../../components/connectors/GoogleAccountConnector.vue'),
  },
  {
    key: 'playstore_account',
    id: 'playstore_account',
    nameKey: 'integrations.providers.playStore',
    category: 'analytics_and_tracking',
    authFamily: 'google_email',
    connect: 'oauth',
    labelFields: ['package_name'],
    logo: 'play_store',
    configComponent: () => import('../../components/connectors/GoogleAccountConnector.vue'),
  },
  {
    key: 'dv360_advertiser',
    id: 'dv360_advertiser',
    nameKey: 'integrations.providers.dv360',
    category: 'paid_advertising',
    authFamily: 'google_email',
    connect: 'oauth',
    labelFields: ['display_name', 'partner_id', 'advertiser_id'],
    logo: 'dv360',
    configComponent: () => import('../../components/connectors/GoogleAccountConnector.vue'),
  },
  {
    key: 'cm360_advertiser',
    id: 'cm360_advertiser',
    nameKey: 'integrations.providers.cm360',
    category: 'paid_advertising',
    authFamily: 'google_email',
    connect: 'oauth',
    // An advertiser row (ORP-26265) leads with its advertiser; a legacy whole-profile row has no
    // `display_name`/`advertiser_id`, so blanks drop and it reads `account_name - account_id` as
    // it always did.
    labelFields: ['display_name', 'advertiser_id', 'account_name', 'account_id'],
    logo: 'cm360',
    configComponent: () => import('../../components/connectors/GoogleAccountConnector.vue'),
  },
]
